"use client"
import styled from "styled-components"
import { Home, Globe, Camera, Film, User, SettingsIcon, Moon, Sun, MessageSquare, Bell, BookOpen, LogIn } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useState, useEffect } from "react"
import NotificationPanel from "./NotificationPanel"

const NavbarContainer = styled.header`
  background-color: ${props => props.theme.navBackground};
  box-shadow: ${props => props.theme.navShadow};
`

const NavbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
  height: 64px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 650px) {
    justify-content: space-between;
    
    > div:last-child {
      justify-content: space-between;
      width: 100%;
    }
  }
  
  @media (max-width: 350px) {
    padding: 0 12px;
    height: 56px;
  }
`

const Logo = styled.div`
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
  
  span:first-child {
    color: ${props => props.theme.textPrimary};
  }
  
  span:last-child {
    color: ${props => props.theme.textSecondary};
    font-size: 12px;
    margin-left: 5px;
  }
  
  @media (max-width: 650px) {
    display: none;
  }
`

const NavMenu = styled.nav`
  display: flex;
  gap: 18px;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 420px) {
    gap: 6px;
  }
`

const NavButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  
  background-color: ${(props) => (props.$active ? props.theme.buttonPrimary : "transparent")};
  color: ${(props) => (props.$active ? "white" : props.theme.textPrimary)};
  
  &:not(:first-child) {
    &:hover {
      background-color: ${(props) => (props.$active ? props.theme.buttonPrimary : props.theme.hoverBackground)};
    }
  }
  svg {
    margin-right: 6px;
  }
  
  @media (max-width: 768px) {
    padding: 8px;
    
    span {
      display: none;
    }
    
    svg {
      margin-right: 0;
    }
  }
  
  @media (max-width: 420px) {
    font-size: 12px;
    
    svg {
      width: 16px;
      height: 16px;
    }
    span {
      font-size: 11px;
    }
  }
  
  @media (max-width: 350px) {
    min-width: 36px;
    min-height: 36px;
  }
`

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: 24px;

  @media (max-width: 768px) {
    gap: 10px;
    margin-left: 16px;
  }

  @media (max-width: 420px) {
    gap: 6px;
    margin-left: 8px;
  }

  @media (max-width: 350px) {
    gap: 4px;
    margin-left: 4px;
  }
`

const IconButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.theme.buttonSecondary};
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.buttonSecondaryHover};
  }
  
  @media (max-width: 768px) {
    &.hide-on-mobile {
      display: none;
    }
  }
  
  @media (min-width: 769px) {
    &.show-on-mobile {
      display: none;
    }
  }
  
  @media (max-width: 420px) {
    width: 32px;
    height: 32px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
  
  @media (max-width: 350px) {
    width: 28px;
    height: 28px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`

const NotificationBadge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  background-color: ${props => props.theme.error};
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
  
  @media (max-width: 420px) {
    font-size: 9px;
    padding: 1px 4px;
    min-width: 14px;
  }
  
  @media (max-width: 350px) {
    font-size: 8px;
    padding: 1px 3px;
    min-width: 12px;
  }
`

const ThemeToggle = styled(IconButton)``

const NotificationPanelContainer = styled.div`
  position: fixed;
  top: 70px;
  right: 20px;
  width: 380px;
  max-height: 500px;
  z-index: 1000;
  
  @media (max-width: 768px) {
    right: 10px;
    left: 10px;
    width: auto;
  }
`

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 999;
`

export default function Navbar({ activePage, setActivePage, onMobileMessageClick, onMobileLoginClick }) {
  const { isLoggedIn } = useAuth()
  const { isDarkMode, toggleTheme, theme } = useTheme()
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)
  const [showNotificationPanel, setShowNotificationPanel] = useState(false)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)

  // 읽지 않은 메시지 및 알림 개수 조회
  useEffect(() => {
    if (isLoggedIn) {
      fetchUnreadCounts()
      // 주기적으로 업데이트 (30초마다)
      const interval = setInterval(fetchUnreadCounts, 30000)
      return () => clearInterval(interval)
    }
  }, [isLoggedIn])

  const fetchUnreadCounts = async () => {
    try {
      // 읽지 않은 메시지 개수
      const messageResponse = await fetch('/messages/unread-count', {
        credentials: 'include'
      })
      const messageData = await messageResponse.json()
      if (messageData.success) {
        setUnreadMessageCount(messageData.count)
      }

      // 읽지 않은 알림 개수
      const notificationResponse = await fetch('/notification/unread-count', {
        credentials: 'include'
      })
      const notificationData = await notificationResponse.json()
      if (notificationData.success) {
        setUnreadNotificationCount(notificationData.count)
      }
    } catch (error) {
      console.error('읽지 않은 개수 조회 실패:', error)
    }
  }

  const toggleNotificationPanel = () => {
    setShowNotificationPanel(!showNotificationPanel)
  }

  const closeNotificationPanel = () => {
    setShowNotificationPanel(false)
  }
  
  const handleMessageClick = () => {
    if (window.innerWidth <= 768) {
      // 모바일에서는 풀스크린 메시지 패널 열기
      onMobileMessageClick && onMobileMessageClick()
    } else {
      // 데스크탑에서는 메시지 페이지로 이동
      setActivePage("messages")
    }
  }
  
  return (
    <NavbarContainer theme={theme}>
      <NavbarContent>
        <Logo theme={theme} onClick={() => setActivePage("home")}>
          <span>ANTSNEST</span>
          <span>DEVELOPER CENTER</span>
        </Logo>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <NavMenu>

            <NavButton theme={theme} $active={activePage === "search"} onClick={() => setActivePage("search")}>
              <Globe size={20} />
              <span>월드</span>
            </NavButton>

            <NavButton theme={theme} $active={activePage === "photos"} onClick={() => setActivePage("photos")}>
              <Camera size={20} />
              <span>사진첩</span>
            </NavButton>

            <NavButton theme={theme} $active={activePage === "media"} onClick={() => setActivePage("media")}>
              <Film size={20} />
              <span>미디어</span>
            </NavButton>

            {/* 로그인한 사용자만 볼 수 있는 메뉴들 */}
            {isLoggedIn && (
              <>
                <NavButton theme={theme} $active={activePage === "feed"} onClick={() => setActivePage("feed")}>
                  <BookOpen size={20} />
                  <span>내피드</span>
                </NavButton>

                <NavButton theme={theme} $active={activePage === "settings"} onClick={() => setActivePage("settings")}>
                  <SettingsIcon size={20} />
                  <span>설정</span>
                </NavButton>
              </>
            )}
          </NavMenu>
          
          <RightActions>
            {isLoggedIn && (
              <>
                <IconButton 
                  theme={theme}
                  onClick={handleMessageClick}
                  title="메시지"
                  className="hide-on-mobile"
                >
                  <MessageSquare size={20} />
                  {unreadMessageCount > 0 && (
                    <NotificationBadge theme={theme}>
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </NotificationBadge>
                  )}
                </IconButton>
                
                <IconButton 
                  theme={theme}
                  onClick={toggleNotificationPanel}
                  title="알림"
                >
                  <Bell size={20} />
                  {unreadNotificationCount > 0 && (
                    <NotificationBadge theme={theme}>
                      {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                    </NotificationBadge>
                  )}
                </IconButton>
              </>
            )}
            
            {/* 모바일 메시지 버튼 */}
            <IconButton 
              theme={theme}
              onClick={onMobileMessageClick}
              title="메시지"
              className="show-on-mobile"
            >
              <MessageSquare size={20} />
              {unreadMessageCount > 0 && (
                <NotificationBadge theme={theme}>
                  {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                </NotificationBadge>
              )}
            </IconButton>
            
            {/* 모바일 로그인 버튼 */}
            <IconButton 
              theme={theme}
              onClick={onMobileLoginClick}
              title={isLoggedIn ? '프로필' : '로그인'}
              className="show-on-mobile"
            >
              {isLoggedIn ? <User size={20} /> : <LogIn size={20} />}
            </IconButton>
            
            <ThemeToggle theme={theme} onClick={toggleTheme} title={isDarkMode ? '라이트 모드로 전환' : '다크 모드로 전환'}>
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </ThemeToggle>
          </RightActions>
        </div>
      </NavbarContent>
      
      {showNotificationPanel && (
        <>
          <Backdrop onClick={closeNotificationPanel} />
          <NotificationPanelContainer>
            <NotificationPanel />
          </NotificationPanelContainer>
        </>
      )}
    </NavbarContainer>
  )
}

